<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import Button from 'primevue/button'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import SrDataTable from '../tables/SrDataTable.vue'
import type { TableColumn } from '@/components/alerts/types'
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import { PaymentsV2Service } from '@/services/payments-v2-service'
import type {
  UserPaymentResponse,
  PaymentServiceAvailabilityResponse,
  PaymentMethodResponse,
} from '@/services/payments-v2-service/types'
import { useMyToast } from '@/composable/useMyToast'
import { useTzDate } from '@/composable/useTzDate'
import axios from 'axios'
import { i18n } from '@/i18n'
import { Divider, Skeleton } from 'primevue'
// import { useAuthStore } from '@/stores/authStore'

const { addToast } = useMyToast()
const { dateFullStr } = useTzDate()
const { t } = i18n.global

const canceling = ref(false)
const creating = ref(false)
const visible = ref(false)
const initLoading = ref(false)
const payment = ref<UserPaymentResponse | null>(null)
const pollInterval = ref<number | null>(null)
const timerInterval = ref<number | null>(null)
const timeLeftFormatted = ref('')

// const props = defineProps<{
// prolong_profile_num?: number
// }>()

const services = ref<PaymentServiceAvailabilityResponse[]>([])
const serviceType = ref<string>('')
const paymentMethods = ref<PaymentMethodResponse[]>([])
const paymentMethodId = ref<number | null>(null)
const targetProfileNum = ref<number | null>(null)
const presetServiceType = ref<string | null>(null)
const prolongProfileNum = ref<number | null>(null)

const paymentHistory = ref<UserPaymentResponse[]>([])
const loadingHistory = ref(false)

const selectedPaymentMethod = computed(() =>
  paymentMethods.value.find((m) => m.id === paymentMethodId.value),
)

const selectedService = computed(() =>
  services.value.find((s) => s.service_type === serviceType.value),
)

// const authStore = useAuthStore()
const statusLabel = (status: string) => {
  const map: Record<string, string> = {
    pending: t('modal.payment.status.pending'),
    paid: t('modal.payment.status.paid'),
    cancelled: t('modal.payment.status.cancelled'),
    expired: t('modal.payment.status.expired'),
  }
  return map[status] || status
}

const serviceLabel = (serviceType: string) => {
  // Try to map using known service names from availability list
  const svc = services.value.find((s) => s.service_type === serviceType)
  if (svc) {
    return t(`modal.payment.services.${svc.name}`)
  }
  return t(`modal.payment.services.${serviceType}`)
}

const columns = computed<TableColumn[]>(() => [
  {
    field: 'created_at',
    header: 'modal.payment.historyDate',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'service_type',
    header: 'modal.payment.historyService',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit' }),
  },
  {
    field: 'amount',
    header: 'modal.payment.historyAmount',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'status',
    header: 'modal.payment.historyStatus',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: (v) => {
      const data = v as { status: string }
      const isPaid = data.status === 'paid'
      const isPending = data.status === 'pending'
      return {
        class: [
          'pit',
          isPaid ? 'success' :
            isPending ? 'warn' :
              'danger'
        ]
      }
    },
  },
  {
    field: 'payment_method',
    header: 'modal.payment.historyMethod',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit' }),
  },
])

const formattingRules = computed(() => ({
  created_at: (v: string) => dateFullStr(v),
  service_type: (v: string) => serviceLabel(v),
  amount: (v: string | number) => String(v),
  status: (v: string) => statusLabel(v),
  payment_method: (v: { name?: string } | null) => v?.name ?? '-',
}))

const addZToEnd = (value: string) => {
  if (!value.endsWith('Z')) {
    value = value + 'Z'
  }
  return new Date(value)
}

const isPending = computed(() => payment.value?.status === 'pending')
// const isUrgent = computed(() => {
//   if (!payment.value?.expires_at) return false
//   const diff = new Date(payment.value.expires_at).getTime() - Date.now()
//   return diff < 5 * 60 * 1000
// })
const walletAddress = computed(() => payment.value?.payment_address?.address ?? '')

const toggle = (profileNum?: number | null) => {
  visible.value = !visible.value
  prolongProfileNum.value = profileNum ?? null
}

const show = async (profileNum?: number | null) => {
  if (visible.value) {
    visible.value = false
  }
  await nextTick()
  visible.value = true
  prolongProfileNum.value = profileNum ?? null
}


const openWithPreset = (serviceTypeValue: string) => {
  presetServiceType.value = serviceTypeValue
  toggle()
}

defineExpose({
  toggle,
  show,
  openWithPreset,
})

watch(visible, (v) => {
  if (v) {
    initModal()
  } else {
    stopPolling()
    stopTimer()
    payment.value = null
    services.value = []
    paymentMethods.value = []
    paymentMethodId.value = null
    paymentHistory.value = []
  }
})

const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value)
    pollInterval.value = null
  }
}

const stopTimer = () => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
  timeLeftFormatted.value = ''
}

const servicesOpts = computed(() => {
  const hideByNames = ['prolong_profile']
  return services.value.filter((s) => !hideByNames.includes(s.name))
})

const startTimer = () => {
  stopTimer()
  if (!payment.value?.expires_at) return

  const update = () => {
    const now = Date.now()
    const end = addZToEnd(payment.value!.expires_at!).getTime()
    const diff = end - now

    if (diff <= 0) {
      stopTimer()
      stopPolling()
      payment.value = null
      addToast({ severity: 'error', message: t('modal.payment.expiredToast') })
      return
    }

    const hours = Math.floor(diff / 3600000)
    const minutes = Math.floor((diff % 3600000) / 60000)
    const seconds = Math.floor((diff % 60000) / 1000)
    timeLeftFormatted.value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  update()
  timerInterval.value = window.setInterval(update, 1000)
}

const startPolling = () => {
  stopPolling()
  pollInterval.value = window.setInterval(() => {
    pollPayment()
  }, 5000)
}

const initModal = async (dontLoad = false) => {
  if (!dontLoad) {
    initLoading.value = true
  }
  loadingHistory.value = true
  try {
    const [servicesRes, methodsRes, lastPaymentRes, historyRes] = await Promise.all([
      PaymentsV2Service.getAvailableServices(),
      PaymentsV2Service.getPaymentMethods(),
      PaymentsV2Service.getLastPayment().catch((thrown) => {
        if (axios.isAxiosError(thrown) && thrown.response?.status === 404) {
          return null
        }
        throw thrown
      }),
      PaymentsV2Service.getPayments().catch(() => null),
    ])

    services.value = servicesRes.data.items.filter(
      (s) => s.can_purchase
    )

    const preset = presetServiceType.value
    const presetService = preset ? services.value.find((s) => s.service_type === preset) : null

    if (prolongProfileNum.value) {
      const prolongService = services.value.find((s) => s.service_type === 'prolong_profile')
      serviceType.value = prolongService?.service_type ?? services.value[0]?.service_type ?? ''
      targetProfileNum.value = prolongProfileNum.value
    } else {
      serviceType.value = presetService?.service_type ?? services.value[0]?.service_type ?? ''
      targetProfileNum.value = null
    }

    presetServiceType.value = null

    paymentMethods.value = methodsRes.data.items
    paymentMethodId.value = paymentMethods.value[0]?.id ?? null

    if (lastPaymentRes) {
      const p = lastPaymentRes.data
      if (p.status === 'pending' && (!p.expires_at || addZToEnd(p.expires_at) > new Date())) {
        payment.value = p
        startPolling()
        startTimer()
      } else {
        payment.value = null
      }
    } else {
      payment.value = null
    }

    if (historyRes) {
      paymentHistory.value = historyRes.data.items
    }
  } catch (thrown) {
    if (axios.isAxiosError(thrown) && thrown.response) {
      const type = thrown.response.data?.detail?.[0]?.type
      if (type) {
        addToast({ severity: 'error', message: t(`modal.payment.errors.${type}`) })
      } else {
        addToast({ severity: 'error', message: t('unexceptedError') })
      }
    } else {
      addToast({ severity: 'error', message: t('unexceptedError') })
    }
  } finally {
    initLoading.value = false
    loadingHistory.value = false
  }
}

const pollPayment = async () => {
  if (!payment.value) return
  try {
    const res = await PaymentsV2Service.getPaymentById(payment.value.id)
    payment.value = res.data
    if (res.data.status === 'paid') {
      stopPolling()
      stopTimer()
      addToast({ severity: 'success', message: t('modal.payment.paidToast') })
      visible.value = false
      // await authStore.check()
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    }
  } catch (thrown) {
    if (axios.isAxiosError(thrown) && thrown.response?.status === 404) {
      payment.value = null
      stopPolling()
    }
  }
}

const createPayment = async () => {
  if (!serviceType.value) {
    addToast({ severity: 'warn', message: t('modal.payment.selectService') })
    return
  }

  const service = selectedService.value
  if (!service || !service.can_purchase) {
    addToast({ severity: 'warn', message: t('modal.payment.serviceUnavailable') })
    return
  }

  if (service.service_type === 'prolong_profile' && !targetProfileNum.value) {
    addToast({ severity: 'warn', message: t('modal.payment.selectProfile') })
    return
  }

  if (!paymentMethodId.value) {
    addToast({ severity: 'warn', message: t('modal.payment.selectMethod') })
    return
  }

  creating.value = true
  try {
    const res = await PaymentsV2Service.createPayment({
      service_type: serviceType.value,
      payment_method_id: paymentMethodId.value,
      target_profile_num: targetProfileNum.value,
    })
    payment.value = res.data
    startPolling()
    startTimer()
    // Refresh history
    const historyRes = await PaymentsV2Service.getPayments().catch(() => null)
    if (historyRes) {
      paymentHistory.value = historyRes.data.items
    }
  } catch (thrown) {
    if (axios.isAxiosError(thrown) && thrown.response) {
      const type = thrown.response.data?.detail?.[0]?.type
      if (type) {
        addToast({ severity: 'error', message: t(`modal.payment.errors.${type}`) })
      } else {
        addToast({ severity: 'error', message: t('unexceptedError') })
      }
    } else {
      addToast({ severity: 'error', message: t('unexceptedError') })
    }
  } finally {
    creating.value = false
  }
}

const cancelCurrentPayment = async () => {
  if (!payment.value) return
  canceling.value = true
  try {
    await PaymentsV2Service.cancelPayment(payment.value.id)
    stopPolling()
    stopTimer()
    payment.value = null
    addToast({ severity: 'success', message: t('modal.payment.cancelled') })
    await initModal(true)
  } catch (thrown) {
    if (axios.isAxiosError(thrown) && thrown.response) {
      const type = thrown.response.data?.detail?.[0]?.type
      if (type) {
        addToast({ severity: 'error', message: t(`modal.payment.errors.${type}`) })
      } else {
        addToast({ severity: 'error', message: t('unexceptedError') })
      }
    } else {
      addToast({ severity: 'error', message: t('unexceptedError') })
    }
  } finally {
    canceling.value = false
  }
}

const copyAddress = async () => {
  if (!walletAddress.value) return
  try {
    await navigator.clipboard.writeText(walletAddress.value)
    addToast({ severity: 'success', message: t('modal.payment.addressCopied') })
  } catch {
    addToast({ severity: 'error', message: t('modal.payment.addressCopyFailed') })
  }
}

// const copyAmount = async () => {
//   if (!payment.value?.amount) return
//   try {
//     await navigator.clipboard.writeText(payment.value?.amount)
//     addToast({ severity: 'success', message: t('modal.payment.amountCopied') })
//   } catch {
//     addToast({ severity: 'error', message: t('modal.payment.amountCopyFailed') })
//   }
// }

onUnmounted(() => {
  stopPolling()
  stopTimer()
})
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:content="p-0" class="w-full md:w-250" pt:header="px-4 py-2"
    :header="$t('modal.payment.title')">

    <div v-if="initLoading">
      <div class="p-4">
        <Skeleton width="100%" class="mb-4" height="54px"></Skeleton>
        <Skeleton width="100px" class="mb-2" height="20px"></Skeleton>
        <Skeleton width="100%" class="mb-4" height="42px"></Skeleton>
        <Skeleton width="100%" class="mb-4" height="42px"></Skeleton>
        <Skeleton width="100%" height="42px"></Skeleton>
      </div>
    </div>
    <template v-else>
      <div v-if="payment" class="flex flex-col gap-4 p-4">

        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-2">
            <label class="text-sm text-muted">{{ $t('modal.payment.amount') }}</label>
            <div class="flex items-center gap-2">
              <code
                class="flex-1 break-all bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-100 p-2 rounded text-sm">
            {{ payment.amount }}
          </code>
              <!-- <Button icon="pi pi-copy" @click="copyAmount" variant="outlined" size="small"
                :aria-label="$t('modal.payment.copy')" /> -->
            </div>
          </div>

          <div v-if="isPending && timeLeftFormatted" class="flex flex-col gap-2">
            <label class="text-sm text-muted">{{ $t('modal.payment.timeLeft') }}</label>
            <div class="flex items-center gap-2">
              <code
                class="flex-1 break-all bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-100 p-2 rounded text-sm">
            {{ timeLeftFormatted }}
          </code>
            </div>
          </div>

        </div>

        <div v-if="walletAddress" class="flex flex-col gap-2">
          <label class="text-sm text-muted">{{ $t('modal.payment.walletAddress') }}</label>
          <div class="flex items-center gap-2">
            <code
              class="flex-1 break-all bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-100 p-2 rounded text-sm">
            {{ walletAddress }}
          </code>
            <Button icon="pi pi-copy" @click="copyAddress" variant="outlined" size="small"
              :aria-label="$t('modal.payment.copy')" />
          </div>
          <p class="text-xs text-muted">
            {{ $t('modal.payment.networkHint') }}
          </p>
        </div>
        <Button v-if="payment && isPending" @click="cancelCurrentPayment" :label="$t('modal.payment.cancel')"
          :loading="canceling" severity="danger" variant="outlined" fluid :disabled="canceling" />
      </div>
      <div v-else class="flex flex-col gap-4 p-4">
        <Message severity="secondary" size="small">
          {{ $t('modal.payment.info') }}
        </Message>

        <div v-if="servicesOpts.length === 0" class="text-center text-muted" size="small">
          {{ $t('modal.payment.noServices') }}
        </div>

        <div v-else class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <!-- <label class="text-sm text-muted">{{ $t('modal.payment.method') }}</label> -->
            <Select v-model="paymentMethodId" :options="paymentMethods" option-value="id" class="w-full">
              <template #option="{ option }">
                <span>{{ option.name }}</span>
              </template>
              <template #value>
                <span v-if="selectedPaymentMethod">{{ selectedPaymentMethod.name }}</span>
              </template>
            </Select>
          </div>

          <div class="flex flex-col gap-2">
            <Select v-if="!prolongProfileNum" v-model="serviceType" :options="servicesOpts" option-value="service_type"
              class="w-full">
              <template #option="{ option }">
                <div>
                  <span>{{ $t(`modal.payment.services.${option.name}`) }}</span>
                  <span class="digit ml-1">({{ option.amount }} USDT)</span>
                </div>
              </template>
              <template #value>
                <div v-if="selectedService">
                  <span>{{ $t(`modal.payment.services.${selectedService.name}`) }}</span>
                  <span class="digit ml-1">({{ selectedService.amount }} USDT)</span>
                </div>
              </template>
            </Select>
            <InputText v-else
              :value="$t('modal.payment.services.prolong_profile') + ` #${prolongProfileNum} ($${selectedService?.amount})`"
              disabled class="w-full" />
          </div>
          <Message severity="secondary" variant="outlined" class="mb-0 mt-0" v-if="selectedService">
            {{ $t(`modal.payment.desc.${selectedService.name}`) }}
          </Message>
          <Button v-if="!payment" @click="createPayment" severity="success" variant="outlined" icon="pi pi-shop"
            :label="$t('modal.payment.create')" fluid :loading="creating" :disabled="creating" />
        </div>

      </div>

      <!-- Payment History -->
      <Divider class="mb-2 mt-0"></Divider>
      <!-- <div class="text-sm font-semibold mb-2 px-2">{{ $t('modal.payment.history') }}</div> -->
      <SrDataTable size="small" :value="paymentHistory" :columns="columns" :loading="loadingHistory"
        :formattingRules="formattingRules" :fakeRowsCount="3">
        <template #empty>
          <div class="text-center text-muted py-3">{{ $t('modal.payment.historyEmpty') }}</div>
        </template>
      </SrDataTable>
    </template>

  </AdaptiveDialog>
</template>
